import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { TabPanel, TabView } from 'primereact/tabview';
import { Badge } from 'primereact/badge';

import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  exportToExcelHtml,
  exportMultipleSheetsToExcelHtml,
  getExcelStatusFormatter,
  getExcelResultFormatter,
  getScheduleResultDisplay
} from '../../utils/exportHelper';

// Actions
import { getInterviewTechnology } from '../../redux/actions/hr';
import { userDetailsAction } from '../../redux/actions/userDetails';

// APIs
import {
  getHRCallLogsReportApi,
  getHRInterviewRoundReportApi,
  getHRInterviewScheduleReportApi,
  getHRStatsApi
} from '../../redux/apis/hr';

// Components
import CustomDataTable from '../../components/DataTable';
import Loader from '../../components/Loader';
import SendReportDialog from '../../components/popup/SendReportDialog';

// Constants & Utils
import {
  CALLSTATUS_MAP,
  DEBOUNCE_TIMEOUT,
  ROUTES,
  INTERVIEW_STATUS,
  INTERVIEW_STATUS_LIST,
  STATUS_INTERVIEW
} from '../../constants/common';
import { wordCapitalize, isDarkMode } from '../../utils/common';
import { debounce } from '../../utils/debounce';

// ─── Constants for Sourcing mix ──────────────────────────────────────────────
const DATE_PILLS = ['Today', 'This Week', 'This Month', 'Custom Range'];

const InterviewStatusBadge = ({ status, row }) => {
  const currentStatus =
    status !== undefined && status !== null && status !== ''
      ? status
      : row?.status !== undefined && row?.status !== null && row?.status !== ''
      ? row?.status
      : row?.Status !== undefined && row?.Status !== null && row?.Status !== ''
      ? row?.Status
      : row?.interviewStatus !== undefined &&
        row?.interviewStatus !== null &&
        row?.interviewStatus !== ''
      ? row?.interviewStatus
      : row?.roundStatus;

  if (currentStatus === undefined || currentStatus === null || currentStatus === '') {
    return <span>--</span>;
  }

  const numStatus = Number(currentStatus);
  const statusObj = !isNaN(numStatus)
    ? INTERVIEW_STATUS_LIST.find((s) => s.code === numStatus) ||
      INTERVIEW_STATUS.find((s) => s.code === numStatus)
    : INTERVIEW_STATUS_LIST.find(
        (s) => s.name?.toLowerCase() === String(currentStatus).toLowerCase()
      ) ||
      INTERVIEW_STATUS.find((s) => s.name?.toLowerCase() === String(currentStatus).toLowerCase());

  const statusCode = !isNaN(numStatus) ? numStatus : statusObj ? statusObj.code : NaN;
  const label = statusObj ? statusObj.name : String(currentStatus);

  let badgeColor = 'pending-badge';

  if (!isNaN(statusCode)) {
    switch (statusCode) {
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_SCHEDULE:
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_SCHEDULE:
      case STATUS_INTERVIEW.MANAGEMENT_SCHEDULE:
      case STATUS_INTERVIEW.PRACTICAL_SCHEDULE:
      case STATUS_INTERVIEW.HR_SCHEDULE:
        badgeColor = 'schedule-badge';
        break;
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_RESCHEDULE:
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_RESCHEDULE:
      case STATUS_INTERVIEW.MANAGEMENT_RESCHEDULE:
      case STATUS_INTERVIEW.HR_RESCHEDULE:
      case STATUS_INTERVIEW.PRACTICAL_RESCHEDULE:
        badgeColor = 'reschedule-badge';
        break;
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_COMPLETE:
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_COMPLETE:
      case STATUS_INTERVIEW.MANAGEMENT_COMPLETE:
      case STATUS_INTERVIEW.PRACTICAL_COMPLETE:
      case STATUS_INTERVIEW.HR_COMPLETE:
        badgeColor = 'complete-badge';
        break;
      case STATUS_INTERVIEW.CANCELLED:
        badgeColor = 'cancel-badge';
        break;
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_PENDING:
      case STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_PENDING:
      case STATUS_INTERVIEW.MANAGEMENT_PENDING:
      case STATUS_INTERVIEW.PRACTICAL_PENDING:
      case STATUS_INTERVIEW.HR_PENDING:
      default:
        badgeColor = 'pending-badge';
        break;
    }
  } else if (typeof currentStatus === 'string') {
    const s = currentStatus.toLowerCase();
    if (s.includes('reschedule')) {
      badgeColor = 'reschedule-badge';
    } else if (s.includes('schedule')) {
      badgeColor = 'schedule-badge';
    } else if (s.includes('complete')) {
      badgeColor = 'complete-badge';
    } else if (s.includes('cancel')) {
      badgeColor = 'cancel-badge';
    } else if (s.includes('reject')) {
      badgeColor = 'cancel-badge';
    } else if (s.includes('select')) {
      badgeColor = 'complete-badge';
    } else {
      badgeColor = 'pending-badge';
    }
  }

  return <Badge className={`white-space-nowrap ${badgeColor}`} value={label} />;
};

const getFilterParams = (activePill, customRange, techFilter) => {
  const durationMap = {
    Today: 'today',
    'This Week': 'week',
    'This Month': 'month',
    'Custom Range': 'custom'
  };
  const duration = durationMap[activePill];

  let startDate = null;
  let endDate = null;
  if (duration === 'custom') {
    const range = customRange && customRange[0] ? customRange : [new Date(), new Date()];
    startDate = dayjs(range[0]).format('YYYY-MM-DD');
    endDate = range[1] ? dayjs(range[1]).format('YYYY-MM-DD') : startDate;
  }

  const technology = techFilter?.length > 0 ? techFilter.join(',') : undefined;

  return { duration, startDate, endDate, technology };
};

// ─── Interview Rounds 3-Level Accordion Funnel ──────────────────────────────────
const RoundFunnel = ({ data }) => {
  return (
    <div className="rpt-funnel-container">
      {/* ── Section 1: Round-wise Summary ── */}
      <div className="rpt-funnel-section">
        <h3 className="rpt-funnel-section__title">
          <i className="pi pi-th-large" /> Round-wise Totals
        </h3>
        <div className="rpt-round-row">
          {data.map((roundItem, ri) => (
            <div key={ri} className="rpt-round-card-container">
              <div className="rpt-round-card">
                {/* Front Face */}
                <div className="rpt-round-card__front">
                  <span className="rpt-round-card__name">
                    {roundItem.round.replace(' Round', '')}
                  </span>
                  <span className="rpt-round-card__count">{roundItem.total}</span>
                </div>

                {/* Back Face */}
                <div className="rpt-round-card__back">
                  <div className="rpt-round-card__back-header">
                    {roundItem.round.replace(' Round', '')}
                  </div>
                  <div className="rpt-round-card__status-list">
                    {roundItem.statuses.map((statusItem, si) => (
                      <div key={si} className="rpt-round-card__status-item">
                        <span
                          className={`rpt-status-dot rpt-status-dot--${statusItem.status.toLowerCase()}`}
                        />
                        <span className="rpt-round-card__status-name">{statusItem.status}</span>
                        <span className="rpt-round-card__status-count">{statusItem.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const transformRoundFunnelData = (backendData) => {
  if (!backendData) return [];
  const { roundWise = {}, technologyWise = {} } = backendData;

  const roundsMapping = [
    { key: 'technical1', name: 'Tech 1 Round' },
    { key: 'practical', name: 'Practical Round' },
    { key: 'technical2', name: 'Tech 2 Round' },
    { key: 'management', name: 'Management Round' },
    { key: 'hr', name: 'HR Round' }
  ];

  return roundsMapping.map(({ key, name }) => {
    const roundStats = roundWise[key] || {
      total: 0,
      selected: 0,
      rejected: 0,
      hold: 0,
      pending: 0
    };
    const statuses = [
      {
        status: 'Selected',
        count: roundStats.selected,
        techs: Object.entries(technologyWise)
          .map(([techName, techVal]) => ({
            name: techName,
            count: techVal.rounds[key]?.selected || 0,
            colorCode: techVal.colorCode,
            darkModeColorCode: techVal.darkModeColorCode
          }))
          .filter((t) => t.count > 0)
      },
      {
        status: 'Rejected',
        count: roundStats.rejected,
        techs: Object.entries(technologyWise)
          .map(([techName, techVal]) => ({
            name: techName,
            count: techVal.rounds[key]?.rejected || 0,
            colorCode: techVal.colorCode,
            darkModeColorCode: techVal.darkModeColorCode
          }))
          .filter((t) => t.count > 0)
      },
      {
        status: 'Hold',
        count: roundStats.hold,
        techs: Object.entries(technologyWise)
          .map(([techName, techVal]) => ({
            name: techName,
            count: techVal.rounds[key]?.hold || 0,
            colorCode: techVal.colorCode,
            darkModeColorCode: techVal.darkModeColorCode
          }))
          .filter((t) => t.count > 0)
      },
      {
        status: 'Pending',
        count: roundStats.pending,
        techs: Object.entries(technologyWise)
          .map(([techName, techVal]) => ({
            name: techName,
            count: techVal.rounds[key]?.pending || 0,
            colorCode: techVal.colorCode,
            darkModeColorCode: techVal.darkModeColorCode
          }))
          .filter((t) => t.count > 0)
      }
    ];

    return {
      round: name,
      total: roundStats.total,
      statuses
    };
  });
};

// ─── Main IndividualHRReport Component ───────────────────────────────────────────
const IndividualHRReport = () => {
  const { ID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Redux state
  const userDetails = useSelector((state) => state.userDetails);
  const { isLoading } = useSelector((state) => state.apiStatus);

  const technologyListData = useSelector((state) => state.hrs.interviewTechnologyType);

  // Filters State — initialized from location state if passed from Admin Reports
  const [techFilter, setTechFilter] = useState(() => location.state?.techFilter || []);
  const [techFilterPending, setTechFilterPending] = useState(
    () => location.state?.techFilter || []
  );
  const [activePill, setActivePill] = useState(() => location.state?.activePill || 'This Month');
  const [customRange, setCustomRange] = useState(() => {
    if (location.state?.customRange?.length) {
      return location.state.customRange.map((d) => new Date(d));
    }
    return [new Date(), new Date()];
  });
  const [activeTab, setActiveTab] = useState(0);
  const [showStickyFilter, setShowStickyFilter] = useState(false);
  const [showMailDialog, setShowMailDialog] = useState(false);

  const debouncedSetTechFilter = useMemo(
    () => debounce((value) => setTechFilter(value), DEBOUNCE_TIMEOUT),
    []
  );

  const handleTechFilterChange = (e) => {
    setTechFilterPending(e.value);
    debouncedSetTechFilter(e.value);
  };

  // Statistics state
  const [statsData, setStatsData] = useState({
    offers: {
      total: 0,
      outcome: { selected: 0, notSelected: 0, onHold: 0, pending: 0 },
      ratings: {
        offered: 0,
        accepted: 0,
        rejected: 0,
        inDiscussion: 0,
        furtherReview: 0,
        selected: 0
      }
    },
    interviewRounds: {
      total: 0,
      selected: 0,
      rejected: 0,
      onHold: 0,
      pending: 0
    },
    calls: { total: 0, breakdown: {}, previousPeriodTotal: 0, percentageChange: 0 },
    interviews: {
      total: 0,
      selected: 0,
      rejected: 0,
      onHold: 0,
      pending: 0,
      previousPeriodTotal: 0,
      percentageChange: 0
    },
    candidates: { total: 0, breakdown: {} }
  });

  // Paginated Call Logs State
  const [callsData, setCallsData] = useState([]);
  const [callsTotalRecords, setCallsTotalRecords] = useState(0);
  const [callsPage, setCallsPage] = useState(1);
  const [callsSize, setCallsSize] = useState(10);

  // Paginated Interview Schedule State
  const [schedulesData, setSchedulesData] = useState([]);
  const [schedulesTotalRecords, setSchedulesTotalRecords] = useState(0);
  const [schedulesPage, setSchedulesPage] = useState(1);
  const [schedulesSize, setSchedulesSize] = useState(10);

  // Round Funnel State
  const [roundFunnelData, setRoundFunnelData] = useState([]);

  // Fetch HR details and technology list on mount
  useEffect(() => {
    if (ID) {
      dispatch(userDetailsAction(ID));
    }
    dispatch(getInterviewTechnology());
  }, [dispatch, ID]);

  // Fetch stats on filter change
  useEffect(() => {
    const fetchStats = async () => {
      if (!ID) return;
      const params = getFilterParams(activePill, customRange, techFilter);
      if (!params) return;
      const { duration, startDate, endDate, technology } = params;

      try {
        const response = await getHRStatsApi({
          hrId: ID,
          duration,
          startDate,
          endDate,
          technology
        });
        if (response?.status === 200) {
          setStatsData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching HR stats:', error);
        toast.error('Error fetching HR stats');
      }
    };

    fetchStats();
  }, [activePill, customRange, techFilter, ID]);

  // Fetch Call Logs
  useEffect(() => {
    const fetchCallLogs = async () => {
      if (!ID || activeTab !== 0) return;
      const params = getFilterParams(activePill, customRange, techFilter);
      if (!params) return;
      const { duration, startDate, endDate, technology } = params;

      try {
        const response = await getHRCallLogsReportApi({
          hrId: ID,
          duration,
          startDate,
          endDate,
          technology,
          page: callsPage,
          size: callsSize
        });
        if (response?.status === 200) {
          setCallsData(response.data.data.data || []);
          setCallsTotalRecords(response.data.data.pagination?.totalRecords || 0);
        }
      } catch (error) {
        console.error('Error fetching HR call logs report:', error);
        toast.error('Error fetching HR call logs report');
      }
    };

    fetchCallLogs();
  }, [activePill, customRange, techFilter, ID, callsPage, callsSize, activeTab]);

  // Fetch Interview Schedule
  useEffect(() => {
    const fetchInterviewSchedule = async () => {
      if (!ID || activeTab !== 1) return;
      const params = getFilterParams(activePill, customRange, techFilter);
      if (!params) return;
      const { duration, startDate, endDate, technology } = params;

      try {
        const response = await getHRInterviewScheduleReportApi({
          hrId: ID,
          duration,
          startDate,
          endDate,
          technology,
          page: schedulesPage,
          size: schedulesSize
        });
        if (response?.status === 200) {
          setSchedulesData(response.data.data.data || []);
          setSchedulesTotalRecords(response.data.data.pagination?.totalRecords || 0);
        }
      } catch (error) {
        console.error('Error fetching HR interview schedule report:', error);
        toast.error('Error fetching HR interview schedule report');
      }
    };

    fetchInterviewSchedule();
  }, [activePill, customRange, techFilter, ID, schedulesPage, schedulesSize, activeTab]);

  // Fetch Interview Round Funnel
  useEffect(() => {
    const fetchInterviewRound = async () => {
      if (!ID || activeTab !== 2) return;
      const params = getFilterParams(activePill, customRange, techFilter);
      if (!params) return;
      const { duration, startDate, endDate, technology } = params;

      try {
        const response = await getHRInterviewRoundReportApi({
          hrId: ID,
          duration,
          startDate,
          endDate,
          technology
        });
        if (response?.status === 200) {
          setRoundFunnelData(transformRoundFunnelData(response.data.data) || []);
        }
      } catch (error) {
        console.error('Error fetching HR interview round report:', error);
        toast.error('Error fetching HR interview round report');
      }
    };

    fetchInterviewRound();
  }, [activePill, customRange, techFilter, ID, activeTab]);

  // Handle floating sticky filter bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyFilter(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Recruiter formatting helper variables
  const isLoadedHR =
    userDetails &&
    String(userDetails.ID || userDetails.id || '').toLowerCase() === String(ID || '').toLowerCase();
  const hrFirstName = userDetails?.FirstName || userDetails?.firstName || '';
  const hrLastName = userDetails?.LastName || userDetails?.lastName || '';
  const hrName =
    isLoadedHR && hrFirstName
      ? `${wordCapitalize(hrFirstName)} ${wordCapitalize(hrLastName)}`
      : 'HR Recruiter';

  const techOptions =
    technologyListData?.map((tech) => ({
      label: tech.Technology,
      value: tech.ID
    })) || [];

  const selectedTechNames = useMemo(() => {
    return techFilterPending
      .map((id) => {
        const opt = techOptions.find((o) => o.value === id);
        return opt ? opt.label : '';
      })
      .filter(Boolean);
  }, [techFilterPending, techOptions]);

  // Calculate dynamic widget states based on API statsData
  const callsCount = statsData.calls.total;
  const interviewsCount = statsData.interviews.total;

  const offersData = statsData.offers || {};
  const offersRatings = offersData.ratings || {
    offered: 0,
    accepted: offersData.accepted || 0,
    rejected: offersData.rejected || 0,
    inDiscussion: 0,
    furtherReview: 0,
    selected: 0
  };
  const offersSelectedTotal =
    offersData.outcome?.selected ?? offersData.selected ?? (offersData.total || 0);

  const interviewRoundsData = statsData.interviewRounds ||
    statsData.interviews || {
      total: 0,
      selected: 0,
      rejected: 0,
      onHold: 0,
      pending: 0
    };

  const getFilteredSourcing = () => {
    const breakdown = statsData.candidates?.breakdown || {};
    const total = statsData.candidates?.total || 0;
    const colors = {
      LinkedIn: '#0A66C2',
      Naukri: '#FF5A00',
      Reference: '#6C63FF',
      Manual: '#22B4BE',
      Career: '#F59E0B'
    };

    // Normalize keys: API returns MANUAL, Naukri, LinkedIn, Employee Reference.
    const keyMap = {
      MANUAL: 'Manual',
      Naukri: 'Naukri',
      LinkedIn: 'LinkedIn',
      'Employee Reference': 'Reference',
      Career: 'Career'
    };

    return Object.entries(keyMap).map(([apiKey, label]) => {
      const count = breakdown[apiKey] || 0;
      const percent = total > 0 ? Math.round((count / total) * 100) : 0;
      return {
        label,
        value: percent,
        color: colors[label] || '#6c757d'
      };
    });
  };

  const sourcingData = getFilteredSourcing();

  // ─── Dynamic labels ──────────────────────────────────────────────────────────
  const getPeriodLabel = () => {
    if (activePill === 'Custom Range') {
      if (customRange && customRange[0]) {
        const start = customRange[0].toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short'
        });
        const end = customRange[1]
          ? customRange[1].toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
          : '';
        return end ? `${start} - ${end}` : `${start}`;
      }
      return 'Custom Range';
    }
    return activePill;
  };

  const getTrendSubtext = () => {
    if (activePill === 'Today') return 'vs yesterday';
    if (activePill === 'This Week') return 'vs last week';
    return 'vs last month';
  };

  // ─── Export Handler ──────────────────────────────────────────────────────────

  const handleActiveTabExport = () => {
    const params = getFilterParams(activePill, customRange, techFilter);
    if (!params) return;
    const { duration, startDate, endDate, technology } = params;

    if (activeTab === 0) {
      toast.info('Preparing call logs report export...');
      getHRCallLogsReportApi({
        hrId: ID,
        duration,
        startDate,
        endDate,
        technology,
        page: 1,
        size: 1000000
      })
        .then((response) => {
          if (response?.status === 200) {
            const allData = response.data.data.data || [];
            exportToExcelHtml({
              fileName: `${hrName.replace(' ', '_')}_Calls`,
              sheetName: 'HR Recruiter Call Logs Report',
              columns: [
                {
                  header: 'Date',
                  key: 'date',
                  formatter: (row) => dayjs(row.date).format('DD/MM/YYYY')
                },
                { header: 'Technology', key: 'technology' },
                { header: 'Candidate Name', key: 'candidateName' },
                {
                  header: 'Call Status',
                  key: 'callStatus',
                  formatter: (row) => CALLSTATUS_MAP[row.callStatus] || row.callStatus
                }
              ],
              data: allData,
              activePill,
              customRange,
              techFilter,
              technologyListData,
              hrName
            });
          }
        })
        .catch((err) => {
          console.error(err);
          toast.error('Failed to export call logs');
        });
    } else if (activeTab === 1) {
      toast.info('Preparing interview schedules report export...');
      getHRInterviewScheduleReportApi({
        hrId: ID,
        duration,
        startDate,
        endDate,
        technology,
        page: 1,
        size: 1000000
      })
        .then((response) => {
          if (response?.status === 200) {
            const allData = response.data.data.data || [];
            exportToExcelHtml({
              fileName: `${hrName.replace(' ', '_')}_Interviews`,
              sheetName: 'HR Recruiter Interview Schedule Report',
              columns: [
                {
                  header: 'Scheduled Date',
                  key: 'scheduledDate',
                  formatter: (row) => dayjs(row.scheduledDate).format('DD/MM/YYYY')
                },
                { header: 'Technology', key: 'technology' },
                { header: 'Candidate Name', key: 'candidateName' },
                {
                  header: 'Interview Date',
                  key: 'interviewDate',
                  formatter: (row) => dayjs(row.interviewDate).format('DD/MM/YYYY')
                },
                { header: 'Status', key: 'status', formatter: getExcelStatusFormatter },
                { header: 'Result', key: 'result', formatter: getExcelResultFormatter }
              ],
              data: allData,
              activePill,
              customRange,
              techFilter,
              technologyListData,
              hrName
            });
          }
        })
        .catch((err) => {
          console.error(err);
          toast.error('Failed to export interview schedules');
        });
    } else if (activeTab === 2) {
      const getRoundsExportDetails = (roundsList) => {
        const hasTechData = roundsList.some((r) =>
          r.statuses?.some((s) => s.techs && s.techs.length > 0)
        );

        if (hasTechData) {
          const columns = [
            { header: 'Round', key: 'Round' },
            { header: 'Status', key: 'Status' },
            { header: 'Technology', key: 'Technology' },
            { header: 'Count', key: 'Count' }
          ];
          const data = roundsList.flatMap((r) =>
            r.statuses.flatMap((s) =>
              s.techs.map((t) => ({
                Round: r.round,
                Status: s.status,
                Technology: t.name,
                Count: t.count
              }))
            )
          );
          return { columns, data };
        }

        const columns = [
          { header: 'Round', key: 'Round' },
          { header: 'Total', key: 'Total' },
          { header: 'Selected', key: 'Selected' },
          { header: 'Rejected', key: 'Rejected' },
          { header: 'Hold', key: 'Hold' },
          { header: 'Pending', key: 'Pending' }
        ];
        const data = roundsList.map((r) => {
          const getStatusCount = (statusName) =>
            r.statuses?.find((s) => s.status === statusName)?.count || 0;
          return {
            Round: r.round,
            Total: r.total || 0,
            Selected: getStatusCount('Selected'),
            Rejected: getStatusCount('Rejected'),
            Hold: getStatusCount('Hold'),
            Pending: getStatusCount('Pending')
          };
        });

        return { columns, data };
      };

      const { columns: roundsCols, data: roundsData } = getRoundsExportDetails(roundFunnelData);

      exportToExcelHtml({
        fileName: `${hrName.replace(' ', '_')}_Funnel`,
        sheetName: 'HR Recruiter Interview Rounds Report',
        columns: roundsCols,
        data: roundsData,
        activePill,
        customRange,
        techFilter,
        technologyListData,
        hrName
      });
    }
  };

  const handleExportAll = async () => {
    const params = getFilterParams(activePill, customRange, techFilter);
    if (!params) return;
    const { duration, startDate, endDate, technology } = params;

    toast.info('Preparing all reports export...');

    try {
      const [callsRes, schedulesRes, roundsRes] = await Promise.all([
        getHRCallLogsReportApi({
          hrId: ID,
          duration,
          startDate,
          endDate,
          technology,
          page: 1,
          size: 1000000
        }),
        getHRInterviewScheduleReportApi({
          hrId: ID,
          duration,
          startDate,
          endDate,
          technology,
          page: 1,
          size: 1000000
        }),
        getHRInterviewRoundReportApi({
          hrId: ID,
          duration,
          startDate,
          endDate,
          technology
        })
      ]);

      const callsAllData = callsRes?.status === 200 ? callsRes.data.data.data || [] : [];
      const schedulesAllData =
        schedulesRes?.status === 200 ? schedulesRes.data.data.data || [] : [];
      const roundsRawData = roundsRes?.status === 200 ? roundsRes.data.data : null;
      const roundsAllData = transformRoundFunnelData(roundsRawData) || [];

      const getRoundsExportDetails = (roundsList) => {
        const hasTechData = roundsList.some((r) =>
          r.statuses?.some((s) => s.techs && s.techs.length > 0)
        );

        if (hasTechData) {
          const columns = [
            { header: 'Round', key: 'Round' },
            { header: 'Status', key: 'Status' },
            { header: 'Technology', key: 'Technology' },
            { header: 'Count', key: 'Count' }
          ];
          const data = roundsList.flatMap((r) =>
            r.statuses.flatMap((s) =>
              s.techs.map((t) => ({
                Round: r.round,
                Status: s.status,
                Technology: t.name,
                Count: t.count
              }))
            )
          );
          return { columns, data };
        }

        const columns = [
          { header: 'Round', key: 'Round' },
          { header: 'Total', key: 'Total' },
          { header: 'Selected', key: 'Selected' },
          { header: 'Rejected', key: 'Rejected' },
          { header: 'Hold', key: 'Hold' },
          { header: 'Pending', key: 'Pending' }
        ];
        const data = roundsList.map((r) => {
          const getStatusCount = (statusName) =>
            r.statuses?.find((s) => s.status === statusName)?.count || 0;
          return {
            Round: r.round,
            Total: r.total || 0,
            Selected: getStatusCount('Selected'),
            Rejected: getStatusCount('Rejected'),
            Hold: getStatusCount('Hold'),
            Pending: getStatusCount('Pending')
          };
        });

        return { columns, data };
      };

      const { columns: roundsExportColumns, data: roundsExportData } =
        getRoundsExportDetails(roundsAllData);

      exportMultipleSheetsToExcelHtml({
        fileName: `${hrName.replace(' ', '_')}_All_Reports`,
        sheets: [
          {
            sheetName: 'Call Logs',
            columns: [
              {
                header: 'Date',
                key: 'date',
                formatter: (row) => dayjs(row.date).format('DD/MM/YYYY')
              },
              { header: 'Technology', key: 'technology' },
              { header: 'Candidate Name', key: 'candidateName' },
              {
                header: 'Call Status',
                key: 'callStatus',
                formatter: (row) => CALLSTATUS_MAP[row.callStatus] || row.callStatus
              }
            ],
            data: callsAllData
          },
          {
            sheetName: 'Interview Schedules',
            columns: [
              {
                header: 'Scheduled Date',
                key: 'scheduledDate',
                formatter: (row) => dayjs(row.scheduledDate).format('DD/MM/YYYY')
              },
              { header: 'Technology', key: 'technology' },
              { header: 'Candidate Name', key: 'candidateName' },
              {
                header: 'Interview Date',
                key: 'interviewDate',
                formatter: (row) => dayjs(row.interviewDate).format('DD/MM/YYYY')
              },
              { header: 'Status', key: 'status', formatter: getExcelStatusFormatter },
              { header: 'Result', key: 'result', formatter: getExcelResultFormatter }
            ],
            data: schedulesAllData
          },
          {
            sheetName: 'Interview Rounds',
            columns: roundsExportColumns,
            data: roundsExportData
          }
        ],
        activePill,
        customRange,
        techFilter,
        technologyListData,
        hrName
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to export all reports');
    }
  };

  // ─── Filter Bar Component ──────────────────────────────────────────────────
  const renderFilterBar = (isSticky = false) => (
    <div
      className={`rpt-filter-bar ${isSticky ? 'rpt-filter-bar--sticky' : ''} ${
        isSticky && showStickyFilter ? 'rpt-filter-bar--sticky-show' : ''
      }`}>
      <div className="rpt-pill-group">
        {DATE_PILLS.map((pill) => (
          <button
            key={pill}
            className={`rpt-pill ${activePill === pill ? 'rpt-pill--active' : ''}`}
            onClick={() => setActivePill(pill)}>
            {pill}
          </button>
        ))}
      </div>
      {activePill === 'Custom Range' && (
        <Calendar
          value={customRange}
          onChange={(e) => setCustomRange(e.value || [new Date(), new Date()])}
          onHide={() => {
            if (customRange && customRange[0] && !customRange[1]) {
              setCustomRange([customRange[0], customRange[0]]);
            }
          }}
          selectionMode="range"
          readOnlyInput
          placeholder="Select date range"
          className="rpt-calendar"
          showIcon
        />
      )}
      <MultiSelect
        value={techFilterPending}
        options={techOptions}
        onChange={handleTechFilterChange}
        placeholder="Filter by Technology"
        display="chip"
        filter
        maxSelectedLabels={2}
        className="rpt-multiselect"
      />
      <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
        <Button
          label="Send Mail"
          icon="pi pi-envelope"
          className="p-button-outlined p-button-sm rpt-send-mail-btn"
          onClick={() => setShowMailDialog(true)}
          style={{ borderColor: '#7367f0', color: '#7367f0' }}
        />
        <Button
          label="Export All"
          icon="pi pi-file-excel"
          className="p-button-success p-button-sm rpt-export-btn"
          onClick={handleExportAll}
        />
      </div>
    </div>
  );

  return (
    <div className="rpt-page">
      {isLoading && !isLoadedHR && <Loader />}

      {/* ─── Header ────────────────────────────────────────────── */}
      <div className="rpt-page-header" style={{ alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 className="rpt-page-title" style={{ margin: 0 }}>
          {hrName}
        </h1>
        <Button
          label="Back to Reports"
          icon="pi pi-chevron-left"
          className="p-button-outlined rpt-sync-btn"
          onClick={() =>
            navigate(ROUTES.ADMIN.REPORTS, {
              state: { activePill, customRange, techFilter }
            })
          }
        />
      </div>

      {/* ─── Static Filters ────────────────────────────────────────────────── */}
      {renderFilterBar(false)}

      {/* ── Stat Widgets ── */}
      <div className="rpt-widgets">
        {/* Widget 1: Sourcing Mix */}
        <div className="rpt-widget rpt-widget--sourcing">
          <div className="rpt-widget__header">
            <span className="rpt-widget__icon">
              <i className="pi pi-share-alt" />
            </span>
            <span className="rpt-widget__title" title="Sourcing Mix">
              Sourcing Mix
            </span>
          </div>
          <div className="rpt-sourcing-bar">
            {sourcingData.map((s) => (
              <div
                key={s.label}
                className="rpt-sourcing-bar__seg"
                style={{ flex: s.value, background: s.color }}
                title={`${s.label}: ${s.value}%`}
              />
            ))}
          </div>
          <div className="rpt-sourcing-legend">
            {sourcingData.map((s) => (
              <span key={s.label}>
                <span className="dot" style={{ background: s.color }} />
                {s.label} <strong>{s.value}%</strong>
              </span>
            ))}
          </div>
        </div>

        {/* Widget 2: Total Calls */}
        <div className="rpt-widget rpt-widget--stat">
          <div className="rpt-widget__header">
            <span className="rpt-widget__icon rpt-widget__icon--calls">
              <i className="pi pi-phone" />
            </span>
            <span className="rpt-widget__title" title="Total Calls">
              Total Calls
            </span>
          </div>
          <div className="rpt-stat-value">{callsCount}</div>
          <div className="rpt-stat-sub">{getPeriodLabel()}</div>
          {activePill !== 'Custom Range' && (
            <div
              className={`rpt-stat-trend ${
                statsData.calls.percentageChange < 0 ? 'rpt-stat-trend--down' : ''
              }`}>
              <i
                className={`pi ${
                  statsData.calls.percentageChange < 0 ? 'pi-arrow-down' : 'pi-arrow-up'
                }`}
              />{' '}
              {Math.abs(statsData.calls.percentageChange)}% {getTrendSubtext()}
            </div>
          )}
        </div>

        {/* Widget 3: Interviews Scheduled */}
        <div className="rpt-widget rpt-widget--stat">
          <div className="rpt-widget__header">
            <span className="rpt-widget__icon rpt-widget__icon--interviews">
              <i className="pi pi-calendar-plus" />
            </span>
            <span className="rpt-widget__title" title="Interviews Scheduled">
              Interviews Scheduled
            </span>
          </div>
          <div className="rpt-stat-value">{interviewsCount}</div>
          <div className="rpt-stat-sub">{getPeriodLabel()}</div>
          {activePill !== 'Custom Range' && (
            <div
              className={`rpt-stat-trend ${
                statsData.interviews.percentageChange < 0
                  ? 'rpt-stat-trend--down'
                  : 'rpt-stat-trend--up'
              }`}>
              <i
                className={`pi ${
                  statsData.interviews.percentageChange < 0 ? 'pi-arrow-down' : 'pi-arrow-up'
                }`}
              />{' '}
              {Math.abs(statsData.interviews.percentageChange)}% {getTrendSubtext()}
            </div>
          )}
        </div>

        {/* Widget 4: Interview Rounds */}
        <div className="rpt-widget rpt-widget--stat">
          <div className="rpt-widget__header">
            <span className="rpt-widget__icon rpt-widget__icon--rounds">
              <i className="pi pi-check-circle" />
            </span>
            <span className="rpt-widget__title" title="Interview Rounds">
              Interview Rounds
            </span>
          </div>
          <div className="rpt-stat-value">{interviewRoundsData.total || 0}</div>
          <div className="rpt-stat-sub">{getPeriodLabel()}</div>
          <div className="rpt-offers-bar">
            {interviewRoundsData.selected > 0 && (
              <div
                className="rpt-offers-bar__seg rpt-offers-bar__seg--accepted"
                style={{ flex: interviewRoundsData.selected }}
                title={`Selected: ${interviewRoundsData.selected}`}
              />
            )}
            {interviewRoundsData.rejected > 0 && (
              <div
                className="rpt-offers-bar__seg rpt-offers-bar__seg--rejected"
                style={{ flex: interviewRoundsData.rejected }}
                title={`Rejected: ${interviewRoundsData.rejected}`}
              />
            )}
            {interviewRoundsData.onHold > 0 && (
              <div
                className="rpt-offers-bar__seg rpt-offers-bar__seg--onhold"
                style={{ flex: interviewRoundsData.onHold }}
                title={`On Hold: ${interviewRoundsData.onHold}`}
              />
            )}
            {interviewRoundsData.pending > 0 && (
              <div
                className="rpt-offers-bar__seg rpt-offers-bar__seg--pending"
                style={{ flex: interviewRoundsData.pending }}
                title={`Pending: ${interviewRoundsData.pending}`}
              />
            )}
          </div>
          <div className="rpt-offers-legend">
            <span>
              <span className="dot dot--accepted" /> Selected{' '}
              <strong>{interviewRoundsData.selected || 0}</strong>
            </span>
            <span>
              <span className="dot dot--rejected" /> Rejected{' '}
              <strong>{interviewRoundsData.rejected || 0}</strong>
            </span>
            <span>
              <span className="dot dot--onhold" /> On Hold{' '}
              <strong>{interviewRoundsData.onHold || 0}</strong>
            </span>
            <span>
              <span className="dot dot--pending" /> Pending{' '}
              <strong>{interviewRoundsData.pending || 0}</strong>
            </span>
          </div>
        </div>

        {/* Widget 5: Offers Pipeline */}
        <div className="rpt-widget rpt-widget--offers">
          <div className="rpt-widget__header">
            <span className="rpt-widget__icon">
              <i className="pi pi-briefcase" />
            </span>
            <span className="rpt-widget__title" title="Offers Pipeline">
              Offers Pipeline
            </span>
          </div>
          <div className="rpt-widget__total">{offersSelectedTotal}</div>
          <div className="rpt-offers-bar">
            {offersRatings.selected > 0 && (
              <div
                className="rpt-offers-bar__seg rpt-offers-bar__seg--selected"
                style={{ flex: offersRatings.selected }}
                title={`Selected: ${offersRatings.selected}`}
              />
            )}
            {offersRatings.offered > 0 && (
              <div
                className="rpt-offers-bar__seg rpt-offers-bar__seg--offered"
                style={{ flex: offersRatings.offered }}
                title={`Offered: ${offersRatings.offered}`}
              />
            )}
            {offersRatings.accepted > 0 && (
              <div
                className="rpt-offers-bar__seg rpt-offers-bar__seg--accepted"
                style={{ flex: offersRatings.accepted }}
                title={`Accepted: ${offersRatings.accepted}`}
              />
            )}
            {offersRatings.rejected > 0 && (
              <div
                className="rpt-offers-bar__seg rpt-offers-bar__seg--rejected"
                style={{ flex: offersRatings.rejected }}
                title={`Rejected: ${offersRatings.rejected}`}
              />
            )}
            {(offersRatings.inDiscussion || 0) + (offersRatings.furtherReview || 0) > 0 && (
              <div
                className="rpt-offers-bar__seg rpt-offers-bar__seg--discussion"
                style={{
                  flex: (offersRatings.inDiscussion || 0) + (offersRatings.furtherReview || 0)
                }}
                title={`In Discussion: ${
                  (offersRatings.inDiscussion || 0) + (offersRatings.furtherReview || 0)
                }`}
              />
            )}
          </div>
          <div className="rpt-offers-legend">
            <span>
              <span className="dot dot--selected" /> Selected{' '}
              <strong>{offersRatings.selected || 0}</strong>
            </span>
            <span>
              <span className="dot dot--offered" /> Offered{' '}
              <strong>{offersRatings.offered || 0}</strong>
            </span>
            <span>
              <span className="dot dot--accepted" /> Accepted{' '}
              <strong>{offersRatings.accepted || 0}</strong>
            </span>
            <span>
              <span className="dot dot--rejected" /> Rejected{' '}
              <strong>{offersRatings.rejected || 0}</strong>
            </span>
            <span>
              <span className="dot dot--discussion" /> In Discussion{' '}
              <strong>
                {(offersRatings.inDiscussion || 0) + (offersRatings.furtherReview || 0)}
              </strong>
            </span>
          </div>
        </div>
      </div>

      {/* ─── Sticky Filter Bar ─────────────────────────────────────────────── */}
      {renderFilterBar(true)}

      {/* ─── Detail Tabs ───────────────────────────────────────────────────── */}
      <div className="rpt-tabs-card">
        <div
          className="rpt-tabs-card__export-wrapper"
          style={{
            display: 'flex',
            justifyContent: activeTab === 1 ? 'space-between' : 'flex-end',
            alignItems: 'center'
          }}>
          <Button
            label="Export Excel"
            icon="pi pi-file-excel"
            className="p-button-success p-button-sm rpt-export-btn"
            onClick={handleActiveTabExport}
          />
        </div>
        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          {/* Tab 1: Call Log */}
          <TabPanel
            header={
              <span>
                <i className="pi pi-phone" /> Call Log
              </span>
            }>
            <CustomDataTable
              data={callsData}
              columns={[
                {
                  field: 'date',
                  header: 'Date',
                  body: (row) => dayjs(row.date).format('DD/MM/YYYY')
                },
                {
                  field: 'technology',
                  header: 'Technology',
                  body: (row) => (
                    <ul className="technology-list">
                      {row.technologies && row.technologies.length > 0
                        ? row.technologies.map((tech) => (
                            <li
                              key={tech.id}
                              style={{
                                backgroundColor: isDarkMode()
                                  ? tech.darkModeColorCode || tech.colorCode
                                  : tech.colorCode
                              }}
                              className="technology-badge">
                              {tech.name}
                            </li>
                          ))
                        : row.technology
                        ? row.technology.split(',').map((techName, idx) => (
                            <li
                              key={idx}
                              className="technology-badge"
                              style={{ backgroundColor: '#6c757d' }}>
                              {techName.trim()}
                            </li>
                          ))
                        : '--'}
                    </ul>
                  )
                },
                { field: 'candidateName', header: 'Candidate Name' },
                {
                  field: 'callStatus',
                  header: 'Call Status',
                  body: (row) => CALLSTATUS_MAP[row.callStatus] || row.callStatus || '--'
                }
              ]}
              lazy={true}
              isHeaderDisable={true}
              totalRecords={callsTotalRecords}
              pageNumber={callsPage}
              pageSize={callsSize}
              onPagination={(p, s) => {
                setCallsPage(p);
                setCallsSize(s);
              }}
              className="rpt-datatable"
              emptyMessage="No records found"
            />
          </TabPanel>

          {/* Tab 2: Interview Schedule */}
          <TabPanel
            header={
              <span>
                <i className="pi pi-calendar" /> Interview Schedule
              </span>
            }>
            <div className="legends fade-in rpt-schedule-legends">
              <div className="legend-label">
                <span className="pending-label" />
                Pending
              </div>
              <div className="legend-label">
                <span className="schedule-label" />
                Schedule
              </div>
              <div className="legend-label">
                <span className="reschedule-label" />
                Reschedule
              </div>
              <div className="legend-label">
                <span className="complete-label" />
                Completed
              </div>
              <div className="legend-label">
                <span className="cancel-label" />
                Cancel
              </div>
            </div>
            <CustomDataTable
              data={schedulesData}
              columns={[
                {
                  field: 'scheduledDate',
                  header: 'Scheduled Date',
                  body: (row) => dayjs(row.scheduledDate).format('DD/MM/YYYY')
                },
                {
                  field: 'technology',
                  header: 'Technology',
                  body: (row) => (
                    <ul className="technology-list">
                      {row.technologies && row.technologies.length > 0
                        ? row.technologies.map((tech) => (
                            <li
                              key={tech.id}
                              style={{
                                backgroundColor: isDarkMode()
                                  ? tech.darkModeColorCode || tech.colorCode
                                  : tech.colorCode
                              }}
                              className="technology-badge">
                              {tech.name}
                            </li>
                          ))
                        : row.technology
                        ? row.technology.split(',').map((techName, idx) => (
                            <li key={idx} className="technology-badge technology-badge--default">
                              {techName.trim()}
                            </li>
                          ))
                        : '--'}
                    </ul>
                  )
                },
                { field: 'candidateName', header: 'Candidate Name' },
                {
                  field: 'interviewDate',
                  header: 'Interview Date',
                  body: (row) => dayjs(row.interviewDate).format('DD/MM/YYYY')
                },
                {
                  field: 'status',
                  header: 'Status',
                  body: (row) => (
                    <InterviewStatusBadge status={row?.status ?? row?.Status} row={row} />
                  )
                },
                {
                  field: 'result',
                  header: 'Result',
                  body: (row) => getScheduleResultDisplay(row)
                }
              ]}
              lazy={true}
              isHeaderDisable={true}
              totalRecords={schedulesTotalRecords}
              pageNumber={schedulesPage}
              pageSize={schedulesSize}
              onPagination={(p, s) => {
                setSchedulesPage(p);
                setSchedulesSize(s);
              }}
              className="rpt-datatable"
              emptyMessage="No records found"
            />
          </TabPanel>

          {/* Tab 3: Interview Rounds */}
          <TabPanel
            header={
              <span>
                <i className="pi pi-sitemap" /> Interview Rounds
              </span>
            }>
            <RoundFunnel data={roundFunnelData} />
          </TabPanel>
        </TabView>
      </div>

      <SendReportDialog
        show={showMailDialog}
        onHide={() => setShowMailDialog(false)}
        periodLabel={getPeriodLabel()}
        selectedTechNames={selectedTechNames}
        filterParams={{
          duration: getFilterParams(activePill, customRange, techFilter)?.duration,
          startDate: getFilterParams(activePill, customRange, techFilter)?.startDate,
          endDate: getFilterParams(activePill, customRange, techFilter)?.endDate,
          technology: getFilterParams(activePill, customRange, techFilter)?.technology,
          hrId: ID
        }}
      />
    </div>
  );
};

export default IndividualHRReport;
